/*
 * ===============================================================================================================
 *                                Copyright 2024, Blue Yonder Group, Inc.
 *                                           All Rights Reserved
 *
 *                               THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
 *                                          BLUE YONDER GROUP, INC.
 *
 *
 *                         The copyright notice above does not evidence any actual
 *                                 or intended publication of such source code.
 *
 * ===============================================================================================================
 */
import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { LuiModal, LuiMultiSelect } from "@jda/lui-common-component-library-mui5";
import { ArrowRefresh } from "@jda/lui-common-icon-library-mui5";
import { Box, Button, Checkbox, FormControlLabel, Grid, IconButton, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from "@mui/material";

import { useStyles } from "./styles";
import { LABELS, MESSAGES, STATUS } from "./constants";
import interpolateString from "../../utility/stringInterpolation";
import { SNACKBAR_ALERT_DEFAULT_PROPS } from "../../shared/constants";
import DisplayContent from "../../shared/displayContent/DisplayContent";
import { selectSelectedCustomer } from "../../store/customer/customerSelector";
import { setSnackBarDisplay } from "../../store/snackBar/snackBarActionCreator";
import { getRealmDetails, getRealmProvisioningSteps } from "../../store/realm/realm";
import { selectRealmDetails, selectSelectedRealm } from "../../store/realm/realmSelector";
import { selectSelectedSubEnterprise } from "../../store/subEnterprise/subEnterpriseSelector";
import useWindowDimensions from "../../shared/windowDimensions/windowDimensions";

const listData: any = [
  // { key: "ALL", displayName: "Select All Steps", checked: false },
  { key: "TM_STEP", displayName: "Traffic Manager Profiles", status: "Failed", checked: false },
  { key: "DEFAULT_USER_STEP", displayName: "Default User", status: "Completed", checked: false },
  { key: "EMAIL_TEMPLATES_STEP", displayName: "Email Templates", status: "In Progress", checked: false },
  { key: "LOGIN_TEMPLATE_STEP", displayName: "Login Templates", status: "Not Started", checked: false },
  { key: "REALM_SETTINGS_STEP", displayName: "Realm Settings", status: "Failed", checked: false },
  { key: "THEME_STEP", displayName: "Theme", status: "Completed", checked: false },
  { key: "JDADELIVERS_STEP", displayName: "JDA Delivers", status: "In Progress", checked: false },
  { key: "CF_DNS_STEP", displayName: "Cloudflare DNS Records", status: "In Progress", checked: false },
  { key: "PORTAL_APP_STEP", displayName: "Portal App", status: "Not Started", checked: false },
  { key: "APIM_SUBSCRIPTION_STEP", displayName: "APIM Subscription", status: "Failed", checked: false },
  { key: "PASSWORD_POLICY_STEP", displayName: "Password Policy", status: "In Progress", checked: false },
  { key: "PVT_DNS_STEP", displayName: "Private DNS Records", status: "Completed", checked: false },
  { key: "METADATA_SYNC_STEP", displayName: "Metadata Sync", status: "Failed", checked: false },
  { key: "REALM_ADMIN_STEP", displayName: "Realm Admin", status: "In Progress", checked: false },
  { key: "DOMAIN_PATCHING_STEP", displayName: "Domain Patching", status: "Not Started", checked: false }
];

const RealmGeneral = () => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const selectedRealm = useSelector(selectSelectedRealm);
  const selectedRealmDetails = useSelector(selectRealmDetails);
  const selectedCustomer = useSelector(selectSelectedCustomer);
  const selectedSubEnterprise = useSelector(selectSelectedSubEnterprise);

  const realmMetaData = selectedRealmDetails?.metaData;

  const [hostName, setHostName] = useState('AUTH');
  const [open, setOpen] = useState<boolean>(false);
  const [selectedSteps, setSelectedSteps] = useState<any[]>([]);
  const { height } = useWindowDimensions(); 

  const handleChange = (
    _event: React.MouseEvent<HTMLElement>,
    hostName: string | null,
  ) => {
    if (hostName !== null) {
      setHostName(hostName);
    }
  };

  useEffect(() => {
    getSelectedRealmDetails();
  }, [selectedRealm?.id, dispatch]);

  useEffect(() => {
    if (selectedRealmDetails?.metaData) {
      setHostName(Object.keys(selectedRealmDetails.metaData)[0] || 'AUTH');
    }
  }, [selectedRealmDetails?.metaData]);

  const getSelectedRealmDetails = async () => {
    if (selectedSubEnterprise?.id && selectedRealm?.id && selectedCustomer?.id) {
      const payload = {
        id: selectedRealm.id,
        customerId: selectedCustomer.id,
        subEnterpriseId: selectedSubEnterprise.id,
      }
      await getRealmDetails(dispatch, payload);
      // await getRealmProvisioningSteps(dispatch, payload);
    }
  };

  const displaySnackbar = (name: string, severity: "success" | "error") => {
    dispatch(
      setSnackBarDisplay({
        ...SNACKBAR_ALERT_DEFAULT_PROPS,
        show: true,
        message: interpolateString([name], LABELS.COPIED_TO_CLIPBOARD),
        severity: severity,
      })
    );
  };

  const hideSnackbar = () => {
    dispatch(
      setSnackBarDisplay({
        ...SNACKBAR_ALERT_DEFAULT_PROPS,
        show: false,
      })
    );
  };

  const showHideSnackbar = (name: string) => {
    hideSnackbar();
    displaySnackbar(name, "success");
  };

  const handleChangeProvisioningSteps = (items: any) => {
    setSelectedSteps(items);
  };

  return (
    <Box className={classes.container} height={height - 250}>
      <Grid container className={classes.root} data-testid="realm-general">
        <Grid item xs={12}>
          <DisplayContent
            testId="customerName"
            data-testid="realmCustomerName"
            content={selectedCustomer?.name || "N/A"}
            contentText={LABELS.CUSTOMER_NAME}
            handleCopyClick={() => showHideSnackbar(LABELS.CUSTOMER_NAME)}
          />
        </Grid>
        <Grid item xs={12}>
          <DisplayContent
            testId="realmName"
            data-testid="realmName"
            contentText={LABELS.REALM_NAME}
            content={selectedRealmDetails.realmName || "N/A"}
            handleCopyClick={() => showHideSnackbar(LABELS.REALM_NAME)}
          />
        </Grid>
        <Grid item xs={12}>
          <DisplayContent
            contentText={LABELS.REALM_DISPLAY_NAME_LABEL}
            content={selectedRealmDetails.displayName || "N/A"}
            testId={"displayName"}
          />
        </Grid>

        <Grid item xs={12}>
          <DisplayContent
            testId="accountKey"
            data-testid="accountKey"
            contentText={LABELS.ACCOUNT_KEY}
            content={selectedRealmDetails?.accountKey || "N/A"}
            handleCopyClick={() => showHideSnackbar(LABELS.ACCOUNT_KEY)}
          />
        </Grid>
        <Grid item xs={12}>
          <DisplayContent
            testId="realmKey"
            data-testid="realmKey"
            contentText={LABELS.REALM_KEY}
            content={selectedRealmDetails?.realmKey || "N/A"}
            handleCopyClick={() => showHideSnackbar(LABELS.REALM_KEY)}
          />
        </Grid>
        <Grid item xs={12}>
          <DisplayContent
            testId="environmentKey"
            data-testid="environmentKey"
            contentText={LABELS.ENVIRONMENT_KEY}
            content={selectedRealmDetails?.envKey || "N/A"}
            handleCopyClick={() => showHideSnackbar(LABELS.ENVIRONMENT_KEY)}
          />
        </Grid>

        <Grid item xs={12}>
          <DisplayContent
            contentText={LABELS.REALM_ENVIRONMENT_TYPE}
            content={selectedRealmDetails.customerEnvType || "N/A"}
            testId={"environment"}
          />
        </Grid>

        <Grid item xs={12}>
          <DisplayContent
            testId={"costCenter"}
            contentText={LABELS.REALM_COST_CENTER}
            content={selectedSubEnterprise?.costCenter || "N/A"}
          />
        </Grid>

        <Grid item xs={12}>
          <DisplayContent
            contentText={LABELS.CONTACT_EMAIL}
            content={selectedRealmDetails.contactEmail || "N/A"}
            testId={"contactEmail"}
          />
        </Grid>

        {selectedRealmDetails.securityDomainId &&
          <Grid item xs={12}>
            <DisplayContent
              contentText={LABELS.SECURITY_DOMAIN_ID}
              content={selectedRealmDetails.securityDomainId || "N/A"}
              testId={"securityDomainId"}
            />
          </Grid>
        }

        {selectedRealm?.envType && (
          <Grid item xs={12}>
            <DisplayContent
              contentText={LABELS.REALM_ENVIRONMENT_TYPE}
              content={selectedRealm?.customerEnvType || "N/A"}
              testId={"realmEnvType"}
            />
          </Grid>
        )}

        <Grid item xs={12}>
          <DisplayContent
            contentText={LABELS.REALM_STATUS}
            content={STATUS[selectedRealmDetails?.status] || STATUS.PENDING}
            testId={"realmReleaseStatus"}
            status={selectedRealmDetails?.status}
          />
          {/* <Tooltip title={LABELS.REALM_RE_PROVISIONING} placement="top">
            <IconButton onClick={() => setOpen(true)} className={classes.refreshButton}>
              <ArrowRefresh />
            </IconButton>
          </Tooltip> */}
        </Grid>


      {/* <Box className={classes.realmDetailsInputContent}>
        {realmMetaData && Object.keys(realmMetaData).length > 0 && (
          <Box sx={{ display: "flex"}}>
            <ToggleButtonGroup exclusive value={hostName} onChange={handleChange} size="small">
              {Object.keys(realmMetaData).length && Object.keys(realmMetaData).map((key) => {
                return (
                  <ToggleButton key={key} value={key} aria-label={`${key} aligned`} className={classes.toggleButtonGroup}>
                    <Typography variant="body2">
                      {`${key.toUpperCase()}`}
                    </Typography>
                  </ToggleButton>
                );
              })}
            </ToggleButtonGroup>
          </Box>
        )} */}
        {realmMetaData && Object.keys(realmMetaData).length > 0 && Object.keys(realmMetaData).map((key) => {
          return (
            <>
              {realmMetaData[key] && realmMetaData[key].realmPublicHostName && (
                <Grid item xs={12}>
                  <DisplayContent
                    testId={"realmPublicHostName"}
                    contentText={`${key} public hostname`}
                    content={realmMetaData[key].realmPublicHostName || "N/A"}
                    handleCopyClick={() => showHideSnackbar(MESSAGES.REALM_PUBLIC_HOST_NAME_COPY)}
                  />
                </Grid>
              )}

          {realmMetaData && realmMetaData[key] && realmMetaData[key].realmPrivateHostName && realmMetaData[key].realmPrivateHostName !== "https://" && (
            <Grid item xs={12}>
              <DisplayContent
                testId={"realmPrivateHostName"}
                contentText={`${key} private hostname`}
                content={realmMetaData[key].realmPrivateHostName || "N/A"}
                handleCopyClick={() => showHideSnackbar(MESSAGES.REALM_PRIVATE_HOST_NAME_COPY)}
              />
            </Grid>
          )}
        </>
          );
        }
        )}
              </Grid>
      {/* </Box> */}
      {/* <LuiModal
        isOpen={open}
        modalTitle={"Realm Re-Provisioning"}
        handleClose={() => setOpen(false)}
        primaryAction={
          <Button variant="contained" color="primary" onClick={() => { }} autoFocus>
            Re-Run
          </Button>
        }
        secondaryAction={
          <Button
            onClick={() => { }}
            variant="outlined"
            color="primary"
            disableElevation
          >
            Cancel
          </Button>
        }
        modalSize={"sm"}
      >
        <>
          <FormControlLabel
            control={
              <Checkbox
                key={"All"}
                onChange={() => { }}
                // style={{ flex: 1 }}
                checked={false}
                name={"Select All"}
                inputProps={{ 'aria-label': 'controlled' }}
              />
            }
            label={"All"}
          />
          <Box>
            {listData.map((item) => {
              return (
                <FormControlLabel
                  control={
                    <Checkbox
                      key={item.key}
                      onChange={() => { }}
                      // style={{ flex: 1 }}
                      checked={item.checked}
                      name={item.displayName}
                      inputProps={{ 'aria-label': 'controlled' }}
                    />
                  }
                  label={item.displayName + (item.status ? ` (${item.status})` : '')}
                />
              );
            })}
          </Box>
        </>
      </LuiModal> */}
    </Box>
  );
};

export default RealmGeneral;
